/**
 * Check-all-that-apply projection.
 *
 * WHY THIS EXISTS
 *
 * Professional Accomplishments is 35 yes/no items across four groups, Honors &
 * Awards is 15, and each item can carry a 500-character detail. Rendering all 50
 * rows — most of them unselected — buries the handful the applicant actually
 * claimed. So a checklist block carries ONLY the selected items.
 *
 * `totalAvailable` is kept alongside them, and that is not decoration. Without a
 * denominator, "3 items" is indistinguishable from a truncated list or a
 * rendering bug, and a reviewer comparing two applicants has no idea whether 3 is
 * a lot. The count comes from the label map, so it tracks the real question set
 * rather than a hardcoded number that would rot the next time OARM revises the
 * list.
 *
 * ORDER FOLLOWS THE LABEL MAP, not the stored object's key order. Key order on a
 * parsed JSON body is whatever the server wrote and is not guaranteed to match
 * the order the applicant saw the questions in. The label maps are declared in
 * screen order, so iterating them keeps the summary reading like the form.
 */

import type { DisplayBlock, DisplayChecklistItem } from '../oasis-display.types';

/** The stored shape of one check-all-that-apply item. */
export interface SelectableItem {
	selected: boolean;
	description?: string;
}

/**
 * Builds a checklist block from a group of selectable items and its label map.
 *
 * `items` is indexed by the same keys as `labels`; anything in `labels` that is
 * missing from `items` counts toward the total but cannot be selected, which is
 * the correct reading of a question the stored body never answered.
 */
export function checklistBlock<K extends string>(
	heading: string | undefined,
	items: Partial<Record<K, SelectableItem | undefined>> | null | undefined,
	labels: Record<K, string>,
): DisplayBlock {
	const keys = Object.keys(labels) as K[];
	const selected: DisplayChecklistItem[] = [];

	for (const key of keys) {
		const item = items?.[key];
		if (!item?.selected) continue;

		const description = item.description?.trim();
		selected.push(description ? { label: labels[key], description } : { label: labels[key] });
	}

	return {
		kind: 'checklist',
		...(heading ? { heading } : {}),
		selected,
		totalAvailable: keys.length,
	};
}

/**
 * Builds a checklist from a group whose items are plain `YesNo` affirmations
 * rather than `{ selected }` objects — acknowledgements and certifications.
 *
 * Every one of these must be `'yes'` for an application to submit, so a total of
 * 7 of 7 is the expected reading and anything less is a genuine finding. That is
 * exactly why the affirmed ones are listed rather than summarized as "all
 * acknowledged": a 6-of-7 count makes a bad row visible instead of asserting
 * something that is not true.
 */
export function affirmationBlock<K extends string>(
	heading: string | undefined,
	answers: Partial<Record<K, string | undefined>> | null | undefined,
	labels: Record<K, string>,
): DisplayBlock {
	const keys = Object.keys(labels) as K[];
	const selected: DisplayChecklistItem[] = keys.filter((key) => answers?.[key] === 'yes').map((key) => ({ label: labels[key] }));

	return {
		kind: 'checklist',
		...(heading ? { heading } : {}),
		selected,
		totalAvailable: keys.length,
	};
}
